import { useParams } from "react-router-dom";
import { widgetToJsonPath } from "shared/constants";
import {
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMultistepForm } from "shared/hooks/useMultistepForm";
import { WidgetType, WidgetStepType, FormFieldType } from "shared/types";
import { isSkippableStep } from "shared/utils";
import FormControls from "./FormControls";
import DynamicForm from "shared/components/dynamicForm/DynamicForm";
import Header from "./Header";
import "styles/singlestocks/questionnaire.css";

const Questionnaire: React.FC = () => {
  const [widgetConfiguration, setWidgetConfiguration] = useState<WidgetType>();

  const params = useParams<string>();

  useEffect(() => {
    // Load the json locally, will be change to fetch from the Questionnaire service
    const loadFormJson = async () => {
      try {
        // Determine the path to fetch
        if (!params.widget) return;

        const jsonPath =
          widgetToJsonPath[
            `${params.widget}` as keyof {
              questionnaire: string;
            }
          ];

        if (!jsonPath) {
          throw new Error("Form name not found");
        }

        // Fetch the combined JSON file
        const response = await fetch(jsonPath);
        if (!response.ok) {
          throw new Error("Failed to fetch JSON");
        }

        const mainJson = await response.json();

        // Fetch all step JSONs concurrently
        const stepPromises = mainJson.steps.map((stepPath: string) =>
          fetch(stepPath).then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed to fetch step JSON: ${response.statusText}`
              );
            }
            return response.json();
          })
        );

        let stepsData = await Promise.all<WidgetStepType[]>(stepPromises);

        stepsData = stepsData.map((stepData: WidgetStepType) => {
          return {
            ...stepData,
            jsonData: {
              fields: stepData.fields,
            },
          };
        });

        // Combine the data
        const combinedFormJson: WidgetType = {
          formTitle: mainJson.formTitle,
          steps: stepsData,
        };

        setWidgetConfiguration(combinedFormJson);
      } catch (err) {
        console.error(err);
      }
    };

    loadFormJson();
  }, []);

  const getStepsElements = useCallback((): {
    title: string;
    element: ReactElement<{
      fields: FormFieldType[];
    }>;
  }[] => {
    if (!widgetConfiguration || !widgetConfiguration.steps) return [];

    const stepsElementsArray = widgetConfiguration.steps.map(
      (step: WidgetStepType) => {
        return {
          title: step.id,
          element: (
            <DynamicForm key={step.id} fields={step.fields} id={step.id} />
          ),
        };
      }
    );

    return stepsElementsArray;
  }, [widgetConfiguration]);

  const steps = useMemo(() => getStepsElements(), [getStepsElements]);

  const { step, back, next, isFirstStep, isLastStep } = useMultistepForm(steps);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const formValues = Object.fromEntries(formData.entries());

    console.log(formValues);

    if (!isLastStep) return next();
  };

  if (widgetConfiguration && widgetConfiguration.steps) {
    const isStepSkippable = isSkippableStep(step);

    return (
      <>
        <Header isFirstStep={isFirstStep} onBackHandler={back} />
        <form
          className="questionnaire-container"
          onSubmit={onSubmit}
          id="widget-form"
        >
          {step.element}
          <FormControls
            isStepSkippable={isStepSkippable}
            onNextHandler={next}
            isLastStep={isLastStep}
          />
        </form>
      </>
    );
  }
};

export default Questionnaire;
